/**
 * SA-460 (workflow UX sweep spec §7.3): list mode is the wireframe's
 * timeline (`.tl`) — a 22px node ring in the entry's hue with the number
 * inside, a 2px rail, the title with the meta beside it, the produced
 * sentence, the AI line. A done node is a ring, the current node solid with
 * an 8px hole, an upcoming node dashed and muted, a re-open dotted in the
 * warning hue with ↺, the closed node a tick. Each entry with a step is a
 * button that opens the drawer. Colour comes from `executorTone` only (D2).
 *
 * SA-1328 (design §3.1): the rows come from `passView` — entry rows, hinges
 * (a dotted warning node and its note) and folds (a pass as one line with
 * Show / Hide). The NOW row is the one tinted row and carries the band's
 * action, a link or a button beside the row's own button, never inside it.
 * Folds open per mount, in component state.
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { fromHere, taskLinkState } from '@/components/process/taskLink'
import { cn } from '@/utils'
import { LANDING_RING_ON_CARD } from './landingRing'
import type { OpenTaskAction } from './openTaskAction'
import type { Fold, Hinge, PassView } from './passes'
import { HINGE_GLYPH, HIDE, NOW, SHOW, foldButtonLabel } from './passesText'
import type { TimelineEntry } from './timelineModel'

const RING = LANDING_RING_ON_CARD
const RAIL = 'before:absolute before:bottom-0 before:left-[10px] before:top-[22px] before:w-[2px] before:bg-border last:before:hidden'
const ROW = 'relative grid grid-cols-[22px_minmax(0,1fr)] gap-x-3 pb-4 text-sm'
const FOCUS = 'rounded-md outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

export interface RecordTimelineProps {
  view: PassView
  onSelect: (stepId: string) => void
  /** The band's `openTaskAction` for the viewer; drawn on the NOW row. */
  nowAction?: OpenTaskAction | null
  onOpenPanel?: (panel: 'feasibility' | 'quotes') => void
  /** The row the landing rings (`data-timeline-key`). */
  ringKey?: string | null
}

function toneClass(entry: TimelineEntry): string {
  switch (entry.state) {
    case 'reopened':
    case 'revised': return 'text-warning-strong'
    case 'upcoming': return 'text-muted-foreground'
    case 'closed': return 'text-success'
    default: return entry.tone?.text ?? 'text-muted-foreground'
  }
}

function Node({ entry }: { entry: TimelineEntry }) {
  if (entry.state === 'current') {
    return (
      <span aria-hidden="true" className="grid size-[22px] place-items-center rounded-full border-2 border-current bg-current">
        <span className="size-2 rounded-full bg-card" />
      </span>
    )
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-[22px] place-items-center rounded-full border-2 border-current bg-card text-[10px] font-bold',
        entry.state === 'upcoming' && 'border-dashed',
        entry.state === 'reopened' && 'border-dotted text-xs',
      )}
    >
      {entry.state === 'reopened' ? '↺' : entry.state === 'closed' ? '✓' : entry.number ?? ''}
    </span>
  )
}

function Row({ entry, onSelect, nowAction, onOpenPanel, ringKey }: { entry: TimelineEntry; onSelect: (stepId: string) => void } & Pick<RecordTimelineProps, 'nowAction' | 'onOpenPanel' | 'ringKey'>) {
  const location = useLocation()
  const now = entry.state === 'current'
  const body = (
    <>
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className={cn('font-semibold', entry.state === 'upcoming' ? 'font-medium text-muted-foreground' : 'text-foreground')}>{entry.heading}</span>
        {now && (
          <span className="rounded-full bg-primary px-2 py-px text-[9px] font-semibold uppercase tracking-wide text-primary-foreground">{NOW}</span>
        )}
        {entry.meta && <span className="text-xs font-normal text-muted-foreground">{entry.meta}</span>}
      </span>
      {entry.produced && <span className="mt-0.5 block text-[12.5px] font-normal text-foreground/80">{entry.produced}</span>}
      {entry.aiLine && <span className="mt-0.5 block text-xs font-normal text-role-ai">{entry.aiLine}</span>}
    </>
  )
  const actionClass = 'mt-1 inline-block text-[12.5px] text-foreground underline-offset-2 hover:underline'
  return (
    <li
      data-timeline-state={entry.state}
      aria-current={now ? 'step' : undefined}
      data-timeline-key={entry.key}
      data-landing={ringKey === entry.key ? 'true' : undefined}
      data-step-id={entry.stepId ?? undefined}
      className={cn(ROW, RAIL, toneClass(entry))}
    >
      <Node entry={entry} />
      <div
        className={cn(
          'min-w-0',
          now && 'rounded-lg border border-primary bg-[color-mix(in_oklab,var(--primary)_6%,var(--card))] px-3 py-2',
          ringKey === entry.key && RING,
        )}
      >
        {entry.stepId ? (
          <button type="button" onClick={() => onSelect(entry.stepId!)} className={cn('block w-full text-left', FOCUS)}>
            {body}
          </button>
        ) : (
          <div>{body}</div>
        )}
        {now && nowAction && (nowAction.kind === 'task' ? (
          <Link to={nowAction.to} state={taskLinkState(fromHere(location))} className={actionClass}>{nowAction.label} →</Link>
        ) : (
          <button type="button" onClick={() => onOpenPanel?.(nowAction.panel)} className={cn(actionClass, 'block text-left')}>{nowAction.label} →</button>
        ))}
      </div>
    </li>
  )
}

function HingeRow({ hinge, onSelect, ringKey }: { hinge: Hinge; onSelect: (stepId: string) => void; ringKey?: string | null }) {
  const body = (
    <>
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-medium">{hinge.heading}</span>
        {hinge.meta && <span className="text-xs font-normal opacity-85">{hinge.meta}</span>}
      </span>
      {hinge.produced && <span className="mt-0.5 block text-[12.5px] font-normal text-foreground/80">{hinge.produced}</span>}
    </>
  )
  return (
    <li
      data-timeline-state={hinge.kind}
      data-timeline-key={hinge.key}
      data-landing={ringKey === hinge.key ? 'true' : undefined}
      className={cn(ROW, RAIL, 'text-warning-strong')}
    >
      <span aria-hidden="true" className="grid size-[22px] place-items-center rounded-full border-2 border-dotted border-current bg-card text-xs font-bold">
        {HINGE_GLYPH[hinge.kind]}
      </span>
      <div className={cn('min-w-0', ringKey === hinge.key && RING)}>
        {hinge.stepId ? (
          <button type="button" onClick={() => onSelect(hinge.stepId!)} className={cn('block w-full text-left', FOCUS)}>{body}</button>
        ) : (
          <div>{body}</div>
        )}
      </div>
    </li>
  )
}

function FoldRow({ fold, open, onToggle, ringKey }: { fold: Fold; open: boolean; onToggle: () => void; ringKey?: string | null }) {
  return (
    <li
      data-timeline-state="fold"
      data-timeline-key={fold.id}
      data-landing={ringKey === fold.id ? 'true' : undefined}
      className={cn(ROW, RAIL, 'text-muted-foreground')}
    >
      <span aria-hidden="true" className="mt-[3px] grid h-[18px] w-6 place-items-center rounded-[5px] border border-border bg-muted text-[10px] font-bold">
        {fold.entries.length}
      </span>
      <div className={cn('flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[12.5px]', ringKey === fold.id && RING)}>
        <span className="font-medium text-foreground">{fold.range}</span>
        {fold.span && <span>{fold.span}</span>}
        <span>{fold.count}</span>
        <button
          type="button"
          aria-expanded={open}
          aria-label={foldButtonLabel(open, fold)}
          onClick={onToggle}
          className={cn('underline underline-offset-2', FOCUS)}
        >
          {open ? HIDE : SHOW}
        </button>
      </div>
    </li>
  )
}

export function RecordTimeline({ view, onSelect, nowAction, onOpenPanel, ringKey }: RecordTimelineProps) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) => setOpened((prev) => {
    const next = new Set(prev)
    if (!next.delete(id)) next.add(id)
    return next
  })
  const row = (e: TimelineEntry) => (
    <Row key={e.key} entry={e} onSelect={onSelect} nowAction={nowAction} onOpenPanel={onOpenPanel} ringKey={ringKey} />
  )
  return (
    <ol data-testid="record-timeline" className="m-0 list-none p-0">
      {view.rows.flatMap((r) => {
        if (r.kind === 'entry') return [row(r.entry)]
        if (r.kind === 'hinge') return [<HingeRow key={r.hinge.key} hinge={r.hinge} onSelect={onSelect} ringKey={ringKey} />]
        const open = opened.has(r.fold.id)
        return [
          <FoldRow key={r.fold.id} fold={r.fold} open={open} onToggle={() => toggle(r.fold.id)} ringKey={ringKey} />,
          ...(open ? r.fold.entries.map(row) : []),
        ]
      })}
      {view.more > 0 && (
        <li className="grid grid-cols-[22px_minmax(0,1fr)] gap-x-3 text-xs text-muted-foreground">
          <span />
          <span>{view.more} more step{view.more === 1 ? '' : 's'}</span>
        </li>
      )}
    </ol>
  )
}
